import React, {
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";
import { BIBLE_DEFAULT_FONT } from "./common/constants/bible";
import { BASE_API_URL } from "./common/endpoints";
import axios from "./axios_config";
import { useLessonComponentClipboard } from "./hooks/use_lesson_component_clipboard";
import { useTextEditorClipboard } from "./hooks/use_text_editor_clipboard";
import { resolveSiteColorTheme } from "./common/utils/resolve_site_color_theme";
import { readSidebarState, writeSidebarField } from "./common/utils/sidebar_storage";
import { describeError } from "./common/utils/error_message";

const AppContext = createContext();

export const useAppState = () => {
	// One read for all three: they come from a single stored object.
	const storedSidebar = readSidebarState();
	const [isAppMenuExpanded, setIsAppMenuExpanded] = useState(storedSidebar.expanded);
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(storedSidebar.collapsed);
	const [sidebarWidth, setSidebarWidth] = useState(storedSidebar.width);
	const [appMenuVisible, setAppMenuVisible] = useState(true);
	const [isMentorRequestEnabled, setIsMentorRequestEnabled] = useState(false);
	const [isHeaderMoreMenuOpen, setIsHeaderMoreMenuOpen] = useState(false);
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
	const [bibleFontSettings, setBibleFontSettings] = useState(
		JSON.parse(localStorage.getItem("bibleFontSettings")) || BIBLE_DEFAULT_FONT,
	);
	const [appColorTheme, setAppColorTheme] = useState(
		JSON.parse(localStorage.getItem("appTheme")) || "LIGHT",
	);
	const siteColorTheme = resolveSiteColorTheme(appColorTheme, window.location.hostname);
	const [textEditorCopiedRaw, setTextEditorCopiedRaw] = useTextEditorClipboard(); // to copy the TextEditor's state
	const [copiedLessonComponent, setCopiedLessonComponent] = useLessonComponentClipboard(); // to copy the lesson element

	useEffect(() => {
		localStorage.setItem("appTheme", JSON.stringify(appColorTheme));
	}, [appColorTheme]);

	useEffect(() => {
		localStorage.setItem("bibleFontSettings", JSON.stringify(bibleFontSettings));
	}, [bibleFontSettings]);

	useEffect(() => {
		writeSidebarField("expanded", isAppMenuExpanded);
	}, [isAppMenuExpanded]);

	useEffect(() => {
		writeSidebarField("collapsed", isSidebarCollapsed);
	}, [isSidebarCollapsed]);

	useLayoutEffect(() => {
		document.documentElement.style.setProperty("--app-sidebar-width", `${sidebarWidth}px`);
		writeSidebarField("width", sidebarWidth);
	}, [sidebarWidth]);

	useEffect(() => {
		const loadSettings = async () => {
			try {
				const response = await axios.get(`${BASE_API_URL}/v1/settings/request_mentor`);

				const isVisible = response.data?.scope_props?.is_visible ?? false;

				setIsMentorRequestEnabled(isVisible);
			} catch (e) {
				console.error(describeError(e));
			}
		};

		loadSettings();
	}, []);

	return {
		isAppMenuExpanded,
		setIsAppMenuExpanded,
		isSidebarCollapsed,
		setIsSidebarCollapsed,
		appMenuVisible,
		setAppMenuVisible,
		isHeaderMoreMenuOpen,
		setIsHeaderMoreMenuOpen,
		isSettingsModalOpen,
		setIsSettingsModalOpen,
		appColorTheme,
		setAppColorTheme,
		siteColorTheme,
		textEditorCopiedRaw,
		setTextEditorCopiedRaw,
		copiedLessonComponent,
		setCopiedLessonComponent,
		bibleFontSettings,
		setBibleFontSettings,
		isMentorRequestEnabled,
		sidebarWidth,
		setSidebarWidth,
	};
};

export const AppProvider = ({ children }) => {
	const {
		isAppMenuExpanded,
		setIsAppMenuExpanded,
		isSidebarCollapsed,
		setIsSidebarCollapsed,
		appMenuVisible,
		setAppMenuVisible,
		isHeaderMoreMenuOpen,
		setIsHeaderMoreMenuOpen,
		isSettingsModalOpen,
		setIsSettingsModalOpen,
		appColorTheme,
		setAppColorTheme,
		siteColorTheme,
		textEditorCopiedRaw,
		setTextEditorCopiedRaw,
		copiedLessonComponent,
		setCopiedLessonComponent,
		bibleFontSettings,
		setBibleFontSettings,
		isMentorRequestEnabled,
		sidebarWidth,
		setSidebarWidth,
	} = useAppState();

	return (
		<AppContext.Provider
			value={{
				isAppMenuExpanded,
				setIsAppMenuExpanded,
				isSidebarCollapsed,
				setIsSidebarCollapsed,
				appMenuVisible,
				setAppMenuVisible,
				isHeaderMoreMenuOpen,
				setIsHeaderMoreMenuOpen,
				isSettingsModalOpen,
				setIsSettingsModalOpen,
				appColorTheme,
				setAppColorTheme,
				siteColorTheme,
				textEditorCopiedRaw,
				setTextEditorCopiedRaw,
				copiedLessonComponent,
				setCopiedLessonComponent,
				bibleFontSettings,
				setBibleFontSettings,
				isMentorRequestEnabled,
				sidebarWidth,
				setSidebarWidth,
			}}
		>
			{children}
		</AppContext.Provider>
	);
};

export const useAppContext = () => {
	return useContext(AppContext);
};

/** The site theme as seen by what is rendered inside: a note presentation switches its own
 *  background, and the modals it opens follow it rather than the site. */
export const SiteColorThemeOverride = ({ theme, children }) => {
	const app = useContext(AppContext);
	const value = useMemo(() => ({ ...app, siteColorTheme: theme }), [app, theme]);
	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
